/**
 * VÉRAULT Portal — Advisor: House Intelligence (Phase 1.1)
 *
 * The firm-wide search lens. One calm bar searches across every
 * record the house knows about: clients, experiences, agreements,
 * forms, supplier confirmations, invoices/payments, dossier records
 * and referrals.
 *
 * This first cut is keyword (regex-based) search powered by the
 * existing /api/admin/records/search endpoint. AI-powered semantic
 * search is reserved for Phase 1.2 — the index structure here is
 * already shaped to graduate to embeddings without UI rework.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  StatusTag,
  PortalButton,
  Empty,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const KIND_LABEL = {
  client: { label: "Client", tone: "neutral" },
  experience: { label: "Experience", tone: "gold" },
  agreement: { label: "Agreement", tone: "neutral" },
  form: { label: "Form", tone: "neutral" },
  booking: { label: "Supplier", tone: "sage" },
  payment: { label: "Payment", tone: "gold" },
  dossier: { label: "Dossier", tone: "neutral" },
  referral: { label: "Referral", tone: "sage" },
};

const FACETS = [
  { id: "all", label: "All" },
  { id: "client", label: "Clients" },
  { id: "experience", label: "Experiences" },
  { id: "agreement", label: "Agreements" },
  { id: "form", label: "Forms" },
  { id: "booking", label: "Suppliers" },
  { id: "payment", label: "Payments" },
  { id: "dossier", label: "Dossier" },
  { id: "referral", label: "Referrals" },
];

const SUGGESTED_QUERIES = [
  "Jackson",
  "Portugal",
  "VLT-E-26",
  "deposit",
  "passport",
  "hotel",
];

function ResultRow({ row }) {
  const kind = KIND_LABEL[row.kind] || { label: row.kind, tone: "neutral" };
  const content = (
    <div className="px-4 py-3 flex items-start gap-3 hover:bg-[hsl(var(--background-paper))]/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusTag tone={kind.tone}>{kind.label}</StatusTag>
          {row.code && (
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {row.code}
            </span>
          )}
        </div>
        <p className="mt-1 font-serif text-[15px] text-[hsl(var(--foreground))] truncate" data-testid={`intel-row-title-${row.kind}-${row.id}`}>
          {row.title || "Untitled"}
        </p>
        {row.subtitle && (
          <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] truncate">
            {row.subtitle}
          </p>
        )}
      </div>
      {row.href && (
        <ArrowRight className="size-3.5 text-[hsl(var(--foreground-muted))] mt-1 shrink-0" strokeWidth={1.5} />
      )}
    </div>
  );
  return row.href ? (
    <Link to={row.href} className="block" data-testid={`intel-row-${row.kind}-${row.id}`}>
      {content}
    </Link>
  ) : (
    <div data-testid={`intel-row-${row.kind}-${row.id}`}>{content}</div>
  );
}

export default function HouseIntelligence() {
  const [query, setQuery] = React.useState("");
  const [facet, setFacet] = React.useState("all");
  const [mode, setMode] = React.useState("keyword"); // "keyword" | "ask"
  const [results, setResults] = React.useState([]);
  const [history, setHistory] = React.useState([]); // [{role, content}]
  const [anchoredIds, setAnchoredIds] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const debounceRef = React.useRef(null);

  const runSearch = React.useCallback(async (q, m, opts = {}) => {
    if (!q.trim()) {
      setResults([]);
      setHistory([]);
      setAnchoredIds([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      if (m === "ask") {
        const body = {
          q: q.trim(),
          limit: 25,
          history: opts.history || [],
          anchored_record_ids: opts.expand ? [] : (opts.anchored || []),
          expand_search: !!opts.expand,
        };
        const { data } = await api.post(`/admin/records/ask`, body);
        setResults(data.records || []);
        const next = [
          ...(opts.history || []),
          { role: "user", content: q.trim() },
          { role: "assistant", content: data.answer || "" },
        ].slice(-8); // keep last 4 turns (8 messages)
        setHistory(next);
        setAnchoredIds(data.anchored_record_ids || []);
      } else {
        const { data } = await api.get(`/admin/records/search`, { params: { q: q.trim(), limit: 80 } });
        setResults(data.rows || []);
        setHistory([]);
        setAnchoredIds([]);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (mode === "keyword") {
      debounceRef.current = setTimeout(() => runSearch(query, "keyword"), 250);
    }
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, mode, runSearch]);

  const onSubmit = (e) => {
    e.preventDefault();
    runSearch(query, mode, { history, anchored: anchoredIds });
  };

  const expandSearch = () => {
    runSearch(query, "ask", { history, expand: true });
  };

  const resetConversation = () => {
    setHistory([]);
    setAnchoredIds([]);
    setQuery("");
    setResults([]);
    setHasSearched(false);
  };

  const filtered = facet === "all" ? results : results.filter((r) => r.kind === facet);
  const counts = results.reduce((acc, r) => {
    acc[r.kind] = (acc[r.kind] || 0) + 1;
    return acc;
  }, {});

  // Latest assistant answer for the answer card.
  const latestAnswer = [...history].reverse().find((m) => m.role === "assistant")?.content || "";

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-8">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Intelligence"
          title="House Intelligence"
          tagline="The firm-wide lens. One search, every record the house knows about."
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded>
          <div className="flex items-center gap-2 mb-3" data-testid="house-intel-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("keyword")}
              className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${mode === "keyword" ? "bg-[hsl(var(--foreground))] text-white" : "bg-white border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-muted))]"}`}
              data-testid="house-intel-mode-keyword"
            >Keyword</button>
            <button
              type="button"
              onClick={() => setMode("ask")}
              className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${mode === "ask" ? "bg-[hsl(var(--foreground))] text-white" : "bg-white border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-muted))]"}`}
              data-testid="house-intel-mode-ask"
            >Ask</button>
            <span className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))] ml-2">
              {mode === "ask"
                ? "Press Enter to ask — answers are grounded in your records."
                : "Type to search across every record in the house."}
            </span>
          </div>
          <form onSubmit={onSubmit} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={mode === "ask"
                ? "Ask the house anything… e.g. 'show all signed agreements for Jackson'"
                : "Search clients, experiences, codes, invoices, dossier records, referrals…"}
              autoFocus
              className="w-full pl-10 pr-4 py-3 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-serif text-[15px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
              data-testid="house-intel-search"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-[hsl(var(--foreground-muted))] animate-spin" strokeWidth={1.5} />
            )}
          </form>

          {!hasSearched && (
            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="house-intel-suggestions">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                Try:
              </span>
              {SUGGESTED_QUERIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQuery(s)}
                  className="px-2.5 py-1 rounded-full bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.05)] font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:bg-white hover:text-[hsl(var(--foreground))]"
                  data-testid={`house-intel-suggest-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </Surface>
      </motion.div>

      {latestAnswer && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded data-testid="house-intel-answer">
            <div className="flex items-start gap-3">
              <Sparkles className="size-4 text-[hsl(var(--accent-gold))] mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                  House answer
                  {anchoredIds.length > 0 && (
                    <span className="ml-2 text-[hsl(var(--foreground-muted))]">
                      · anchored to {anchoredIds.length} record{anchoredIds.length === 1 ? "" : "s"}
                    </span>
                  )}
                </p>
                {history.length > 2 && (
                  <ul className="mt-2 space-y-1.5">
                    {history.slice(0, -2).map((turn, i) => (
                      <li
                        key={i}
                        className={`font-accent italic text-[12.5px] ${turn.role === "user" ? "text-[hsl(var(--foreground-muted))]" : "text-[hsl(var(--foreground-secondary))]"}`}
                        data-testid={`house-intel-history-${i}`}
                      >
                        <span className="font-mono not-italic text-[10px] uppercase tracking-[0.18em] mr-2">
                          {turn.role === "user" ? "You" : "House"}
                        </span>
                        {turn.content}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 font-serif text-[15px] leading-relaxed text-[hsl(var(--foreground))]">
                  {latestAnswer}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <p className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))]">
                    Grounded in {results.length} record{results.length === 1 ? "" : "s"} · recall only · no edits made.
                  </p>
                  {anchoredIds.length > 0 && (
                    <button
                      type="button"
                      onClick={expandSearch}
                      className="px-2.5 py-1 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                      data-testid="house-intel-expand-search"
                    >
                      Expand search
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={resetConversation}
                    className="px-2.5 py-1 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                    data-testid="house-intel-reset"
                  >
                    New conversation
                  </button>
                </div>
              </div>
            </div>
          </Surface>
        </motion.div>
      )}

      {hasSearched && results.length > 0 && (
        <motion.div variants={fadeInUp}>
          <div className="flex items-center gap-1 flex-wrap" data-testid="house-intel-facets">
            {FACETS.map((f) => {
              const n = f.id === "all" ? results.length : (counts[f.id] || 0);
              if (f.id !== "all" && n === 0) return null;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFacet(f.id)}
                  className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                    facet === f.id
                      ? "bg-[hsl(var(--foreground))] text-white"
                      : "bg-white border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  }`}
                  data-testid={`house-intel-facet-${f.id}`}
                >
                  {f.label} <span className="opacity-60">· {n}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      <motion.div variants={fadeInUp}>
        {hasSearched ? (
          filtered.length === 0 ? (
            <Empty
              title={loading ? "Searching the house…" : "Nothing matches yet."}
              hint={loading ? "" : "Try a code (VLT-E-26-…), a name, or a confirmation number."}
              icon={<Search className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <Surface variant="default" padded={false} data-testid="house-intel-results">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {filtered.map((r) => (
                  <li key={`${r.kind}-${r.id}`}>
                    <ResultRow row={r} />
                  </li>
                ))}
              </ul>
            </Surface>
          )
        ) : (
          <Surface variant="paper" padded data-testid="house-intel-overview">
            <div className="flex items-start gap-3">
              <Sparkles className="size-4 text-[hsl(var(--accent-gold))] mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0">
                <Heading level="eyebrow">What lives in House Intelligence</Heading>
                <p className="mt-2 font-serif text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
                  Clients · Experiences · Internal codes · Agreements · Forms ·
                  Supplier confirmations · Invoices &amp; planning fees · Dossier
                  records · Referrals.
                </p>
                <p className="mt-2 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                  Search is keyword-only today; semantic AI search arrives in Phase 1.2.
                </p>
              </div>
            </div>
          </Surface>
        )}
      </motion.div>
    </motion.div>
  );
}
